import { LocalDate } from '@js-joda/core';

import { VisitSettlement } from '@dreryk/egabinet-financial-settlement-frontend-api';
import { TaxRate } from '@dreryk/egabinet-private-medical-service-frontend-api';

import { Grid, GridAreas, Heading, StyledFlex } from '@dreryk/react-components';

import { DescriptionField } from '../../../components/DescriptionField';
import { PaymentDateField } from '../../../components/PaymentDateField';
import { TaxExemptionBasisField } from '../../../components/TaxExemptionBasisField';
import { invoiceCreateModalTranslationKeys } from '../../../translations';
import { InvoiceCreateFormRegisterControllerProp } from '../InvoiceCreateForm.types';
import { getInvoicePaymentMethod } from '../../../shared/getInvoicePaymentMethod';

import { InvoiceItemsTable } from './InvoiceItemsTable';
import { InvoiceItemsTableBean } from './InvoiceItemsTable.types';
import { PaidAmountTextView } from './PaidAmountTextView';
import { PaymentMethodTextView } from './PaymentMethodTextView';
import { VisitDateTextView } from './VisitDateTextView';

type SERVICES_GRID_AREA = 'visitDate' | 'paymentMethod' | 'paidAmount' | 'paymentDate' | 'taxExemptionBasis';

const SERVICES_GRID_COLUMNS = 3;
const GRID_GAP = '24px 36px';

const SERVICES_GRID_AREAS: GridAreas<SERVICES_GRID_AREA, typeof SERVICES_GRID_COLUMNS> = {
   custom: {
      template: ['visitDate paymentMethod paidAmount', 'paymentDate taxExemptionBasis taxExemptionBasis'],
      gridTemplateColumns: '174px 174px 1fr',
      gap: GRID_GAP,
   },
};

type InvoiceCreateFormServicesSectionProps = InvoiceCreateFormRegisterControllerProp & {
   readonly invoiceItems: InvoiceItemsTableBean[] | undefined;
   readonly taxRates: TaxRate[];
   readonly visitDate: LocalDate;
   readonly visitSettlement: VisitSettlement;
   readonly receiptAvailableForInvoice: boolean;
};

export const InvoiceCreateFormServicesSection = (props: InvoiceCreateFormServicesSectionProps): JSX.Element => {
   const { invoiceItems, register, taxRates, visitDate, visitSettlement, receiptAvailableForInvoice } = props;

   return (
      <section>
         <Heading
            content={invoiceCreateModalTranslationKeys.servicesDetails}
            testId={'servicesDetails'}
            type='h2'
            margin={'18px'}
         />
         <StyledFlex gap={32} direction={'column'}>
            <Grid<SERVICES_GRID_AREA, typeof SERVICES_GRID_COLUMNS>
               areas={SERVICES_GRID_AREAS}
               testId={'servicesGrid'}
               paidAmount={<PaidAmountTextView paidAmount={visitSettlement.paymentAmount!} />}
               paymentDate={<PaymentDateField register={register} disabled={receiptAvailableForInvoice} />}
               paymentMethod={
                  <PaymentMethodTextView paymentMethod={getInvoicePaymentMethod(visitSettlement.paymentMethod)} />
               }
               visitDate={<VisitDateTextView visitDate={visitDate} />}
               taxExemptionBasis={<TaxExemptionBasisField register={register} />}
            />
            <InvoiceItemsTable
               register={register}
               invoiceItems={invoiceItems}
               taxRates={taxRates}
               receiptAvailableForInvoice={receiptAvailableForInvoice}
               practitionerId={visitSettlement.medicalServicesPerformedByPractitioner?.id}
            />
            <DescriptionField register={register} />
         </StyledFlex>
      </section>
   );
};
